import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../shared/ui/Button';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { Spinner } from '../../../shared/ui/Spinner';
import { IncidentDetails } from '../components/IncidentDetails';
import { IncidentFilters } from '../components/IncidentFilters';
import { IncidentTable } from '../components/IncidentTable';
import { useIncident, useIncidents } from '../hooks/useIncidents';
import styles from './MonitoringPage.module.css';

// PARA-129, AC-14: страница «Мониторинг» — отдельный раздел рядом с «Журналом»,
// доступна любому активному админу. Существующий /logs не затрагивается (AC-21).
export function MonitoringPage(): JSX.Element {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const {
    filters,
    items,
    total,
    offset,
    pageSize,
    hasPrevious,
    hasNext,
    isLoading,
    error,
    setFilter,
    setOffset,
    acknowledge,
  } = useIncidents();
  const detail = useIncident(selectedId);

  return (
    <section>
      <PageHeader title={t('monitoring.title')} />
      <div className={styles.stack}>
        <IncidentFilters
          dateFrom={filters.date_from ?? ''}
          dateTo={filters.date_to ?? ''}
          severity={filters.severity ?? ''}
          category={filters.category ?? ''}
          incidentType={filters.incident_type ?? ''}
          acknowledged={filters.acknowledged === undefined ? '' : String(filters.acknowledged)}
          onChange={setFilter}
        />
        {/* Сбой загрузки нельзя показывать как «записей нет»: на странице
            мониторинга это самое вводящее в заблуждение сообщение. */}
        {error ? (
          <p className={styles.error} role="alert">
            {t('monitoring.error')}
          </p>
        ) : isLoading ? (
          <Spinner />
        ) : (
          <IncidentTable incidents={items} selectedId={selectedId} onSelect={setSelectedId} />
        )}
        <div className={styles.pagination}>
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasPrevious}
            onClick={() => setOffset(Math.max(0, offset - pageSize))}
          >
            {t('monitoring.pagination.previous')}
          </Button>
          <span className={styles.pageInfo}>
            {t('monitoring.pagination.info', {
              from: total === 0 ? 0 : offset + 1,
              to: Math.min(offset + pageSize, total),
              total,
            })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasNext}
            onClick={() => setOffset(offset + pageSize)}
          >
            {t('monitoring.pagination.next')}
          </Button>
        </div>
        {selectedId === null ? null : (
          <IncidentDetails
            incident={detail.data}
            isLoading={detail.isLoading}
            isAcknowledging={acknowledge.isPending}
            onAcknowledge={(incidentId) => acknowledge.mutate(incidentId)}
          />
        )}
      </div>
    </section>
  );
}
